import React, { useState,useEffect,useRef } from "react";
import Swal from "sweetalert2";
import ConfirmModal from "./ConfirmModal";

const lifelineIcon = (themeData, key, defaultIcon) =>
  themeData?.lifelineImages?.[key]
    ? process.env.REACT_APP_S3_PATH + themeData.lifelineImages[key]
    : defaultIcon;

const lifelineName = (themeData, key, defaultName) =>
  themeData?.lifelineNames?.[key] || defaultName;

export function Lifelines({
  lifelinesState,
  flipQuestion,
  currentQuestion,
  answerFeedback,
  onFlipUsed,
  onExpertUsed,
  onDoubleDipUsed,
  onFiftyUsed,
  themeData,
  usedInThisQuestion,
  questionIndex
}) {
  const {
    lifelines,
    flip,
    expert,
    double: doubleDip,
    fifty_fifty: fiftyFifty,
  } = themeData;

  const [modal, setModal] = useState(null);

  const usedLocalRef = useRef({ double: false, fifty: false });

  // Reset local-per-question guard whenever the current question changes
  useEffect(() => {
    usedLocalRef.current = { double: false, fifty: false };
  }, [questionIndex]);

  // If lifelines feature is turned off entirely, render nothing
  if (!lifelines) return null;

  const useFlip = () => {
    if (!flipQuestion || lifelinesState[0] || answerFeedback) return;
    setModal({
      title: `Use ${lifelineName(themeData, "flip", "Flip")}?`,
      text: "Swap this question with your reserved one.",
      confirmText: "Yes",
      showCancel: true,
      icon: lifelineIcon(themeData, "flip", "/lifelines/flip.png"),
      onConfirm: () => {
        onFlipUsed();
        setModal(null);
      },
      onCancel: () => setModal(null),
    });
  };

  const useExpert = () => {
    if (lifelinesState[1] || answerFeedback) return;
    const hint = currentQuestion.options[currentQuestion.answer_id - 1];
    setModal({
      title: `Use ${lifelineName(themeData, "expert", "Expert Advice")}?`,
      text: "You will see a hint to the correct answer.",
      confirmText: "Yes",
      showCancel: true,
      icon: lifelineIcon(themeData, "expert", "/lifelines/expert-advice.png"),
      onConfirm: () => {
        // show hint modal
        setModal({
          title: `${lifelineName(themeData, "expert", "Expert")} says:`,
          text: hint,
          confirmText: "OK",
          showCancel: false,
          onConfirm: () => {
            onExpertUsed();
            setModal(null);
          },
        });
      },
      onCancel: () => setModal(null),
    });
  };

 const useDoubleDip = () => {
  if (lifelinesState[2] || answerFeedback) return;

  // Block if 50:50 already used in THIS question (parent OR local immediate)
  if (usedInThisQuestion?.fifty || usedLocalRef.current.fifty) {
    Swal.fire({
      icon: "warning",
      title: `${lifelineName(themeData, "fifty_fifty", "50:50")} already used!`,
      text: `You cannot use ${lifelineName(themeData, "double", "Double Dip")} on the same question.`,
    });
    return;
  }

  setModal({
    title: `Use ${lifelineName(themeData, "double", "Double Dip")}?`,
    text: "Pick two options instead of one.",
    confirmText: "Yes",
    showCancel: true,
    icon: lifelineIcon(themeData, "double", "/lifelines/double-dip.png"),
    onConfirm: () => {
      // set local immediate guard so other lifelines are blocked instantly
      usedLocalRef.current.double = true;

      // call parent handler which sets parent per-question flag & global state
      onDoubleDipUsed();

      setModal(null);
    },
    onCancel: () => setModal(null)
  });
};


  const useFifty = () => {
  if (lifelinesState[3] || answerFeedback) return;

  // Block if Double Dip already used in THIS question (parent OR local immediate)
  if (usedInThisQuestion?.double || usedLocalRef.current.double) {
    Swal.fire({
      icon: "warning",
      title: `${lifelineName(themeData, "double", "Double Dip")} already used!`,
      text: `You cannot use ${lifelineName(themeData, "fifty_fifty", "50:50")} on the same question.`,
    });
    return;
  }

  setModal({
    title: `Use ${lifelineName(themeData, "fifty_fifty", "50:50")}?`,
    text: "Two wrong answers will be removed.",
    confirmText: "Yes",
    showCancel: true,
    icon: lifelineIcon(themeData, "fifty_fifty", "/lifelines/50.png"),
    onConfirm: () => {
      // set local immediate guard
      usedLocalRef.current.fifty = true;

      // call parent handler which sets parent per-question flag & global state
      const correct = currentQuestion.answer_id;
      const options = currentQuestion.options.map((_, i) => i + 1);
      const wrongs = options.filter((i) => i !== correct);
      const toRemove = wrongs.sort(() => Math.random() - 0.5).slice(0, 2);

      onFiftyUsed(toRemove);
      setModal(null);
    },
    onCancel: () => setModal(null)
  });
};

  // Define each lifeline with its availability flag and handler
  const lifelineConfigs = [
    {
      enabled: flip,
      handler: useFlip,
      used: lifelinesState[0],
      icon: lifelineIcon(themeData, "flip", "/lifelines/flip.png"),
      text: lifelineName(themeData, "flip", "flip"),
    },
    {
      enabled: expert,
      handler: useExpert,
      used: lifelinesState[1],
      icon: lifelineIcon(themeData, "expert", "/lifelines/expert-advice.png"),
      text: lifelineName(themeData, "expert", "expert advice"),
    },
    {
      enabled: doubleDip,
      handler: useDoubleDip,
      used: lifelinesState[2],
      icon: lifelineIcon(themeData, "double", "/lifelines/double-dip.png"),
      text: lifelineName(themeData, "double", "double dip"),
    },
    {
      enabled: fiftyFifty,
      handler: useFifty,
      used: lifelinesState[3],
      icon: lifelineIcon(themeData, "fifty_fifty", "/lifelines/50.png"),
      text: lifelineName(themeData, "fifty_fifty", "50:50"),
    },
  ];

  return (
    <div className="quiz-lifelines">
      <div className="quiz-lifelines-title">LIFELINES</div>
      <div className="quiz-question-lifelines-container">
        {lifelineConfigs
          .filter((l) => l.enabled)
          .map((l, i) => (
            <div className="quiz-lifelines-holder">
              <div
                key={i}
                disabled={l.used === 1 || Boolean(answerFeedback)}
                onClick={l.handler}
              >
                <img
                  src={l.icon}
                  alt={`${l.text} lifeline`}
                  className={l.used ? "quiz-lifeline--used" : "quiz-lifeline"}
                />
              </div>
              <div className="quiz-lifeline-text">{l.text}</div>
            </div>
          ))}
      </div>

      <ConfirmModal
        isOpen={!!modal}
        title={modal?.title}
        text={modal?.text}
        confirmText={modal?.confirmText}
        cancelText={modal?.cancelText}
        showCancel={modal?.showCancel ?? true}
        icon={modal?.icon}
        onConfirm={modal?.onConfirm}
        onCancel={modal?.onCancel}
      />
    </div>
  );
}
